 import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Select from "react-select";
import api from "../../api/api";
import { toast } from "react-toastify";
import Attachments from "../../UserControls/Attachments/Attachments";

const CalibrationLog = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const editData = location.state?.editData || null;

  const [asset, setAsset] = useState("");
  const [certificateNumber, setCertificateNumber] = useState("");
  const [certificatePath, setCertificatePath] = useState("");
  const [result, setResult] = useState("");
  const [comments, setComments] = useState("");
  const [serInvNo, setSerInvNo] = useState("");
  const [newNextDueDate, setNewNextDueDate] = useState("");
  const [calibrationDate, setCalibrationDate] = useState("");

  const [assetOptions, setAssetOptions] = useState([]);
  const [assetCalibrationOptions, setAssetCalibrationOptions] = useState([]);
  const [partyOptions, setPartyOptions] = useState([]);
  const [resultOptions, setResultOptions] = useState([]);
  const [serInvNoOptions, setSerInvNoOptions] = useState([]);
  const [selectedAssetData, setSelectedAssetData] = useState({});
  const userName = sessionStorage.getItem("userName");

  // ✅ Pre-fill edit mode
  useEffect(() => {
    if (editData) {
      setAsset(editData.AssetId || "");
      setCertificateNumber(editData.CertificateNumber || "");
      setResult(editData.Result || "");
      setComments(editData.Comments || "");
      setSerInvNo(editData.SerInvNo || "");
      setCalibrationDate(editData.CalibrationDate?.split("T")[0] || "");
      setNewNextDueDate(editData.NewNextDueDate?.split("T")[0] || "");

      setSelectedAssetData({
        LogId: editData.LogId || 0,
        AssetID: editData.AssetId || 0,
        Vid: editData.Vid || 0,
        AstCalId: editData.AstCalId || 0,
        SerInvNo: editData.SerInvNo || "",
        DocNo: editData.DocNo || "",
        SerCpId: editData.SerCpId || 0,
        OrderType: editData.OrderType || "",
      });
    }
    // eslint-disable-next-line
  }, [editData]);

  // --- Load Assets ---
  useEffect(() => {
    const fetchAssets = async () => {
      try {
        const response = await api.GetCalibrationlogAPI(userName);
        const data = response?.Table || [];
        setAssetOptions(data);
      } catch (error) {
        console.error(error);
        toast.error("Failed to fetch assets.");
      }
    };
    fetchAssets();
  }, []);

  // --- Load Asset Calibration, Party, Result ---
  useEffect(() => {
    if (!asset) return;

    const fetchAssetCalibration = async () => {
      try {
        const res = await api.GetAssetCaliblogAPI(asset, userName);

        const table0 = res?.Table || [];
        const table1 = res?.Table1 || [];
        const table2 = res?.Table2 || [];

        setAssetCalibrationOptions(table0);
        setPartyOptions(table1);
        setResultOptions(table2);

        if (table0.length > 0 && !editData) {
          const first = table0[0];
          setSelectedAssetData({
            ...selectedAssetData,
            AstCalId: first.AstCalId,
            AssetID: first.AssetID,
            CTID: first.CTID,
            CtypeName: first.CtypeName,
            IntervalDays: first.IntervalDays,
            LastCalibrationDate: first.LastCalibrationDate,
            NextDueDate: first.NextDueDate,
            Status: first.Status,
            Vid: first.Vid,
            VCode: first.VCode,
            Vname: first.Vname,
            AssetCalibration: first.CtypeName,
            Party: table1[0]?.Vname || "",
            Result: table2[0]?.Result || "",
            DocNo: first.DocNo || "",
          });
          setCalibrationDate(first.LastCalibrationDate?.split("T")[0] || "");
          setNewNextDueDate(first.NextDueDate?.split("T")[0] || "");
          setResult(table2[0]?.Result || "");
        }
      } catch (error) {
        console.error(error);
        toast.error("Failed to load calibration details.");
      }
    };

    fetchAssetCalibration();
    // eslint-disable-next-line
  }, [asset]);

  // --- Load Service RefNo ---
  useEffect(() => {
    const fetchServiceRefNos = async () => {
      if (!selectedAssetData?.Vid) return;
      try {
        const res = await api.GetServiceInvoiceAPI(selectedAssetData.Vid);
        const table =
          res?.GetServiceInvoiceAPIjs?.Table ||
          res?.Table ||
          res?.table ||
          res?.data ||
          [];
        setSerInvNoOptions(table);
      } catch (error) {
        console.error(error);
        toast.error("Failed to load Service RefNo data.");
      }
    };
    fetchServiceRefNos();
  }, [selectedAssetData?.Vid]);

  const handleAssetChange = (selected) => {
    setAsset(selected?.value || "");
    setSelectedAssetData({});
    setAssetCalibrationOptions([]);
  };

  const handleAssetCalibrationChange = (selected) => {
    const selectedItem = assetCalibrationOptions.find(
      (a) => a.AstCalId === selected?.value
    );
    if (selectedItem) {
      setSelectedAssetData({
        ...selectedAssetData,
        AstCalId: selectedItem.AstCalId,
        AssetID: selectedItem.AssetID,
        CTID: selectedItem.CTID,
        CtypeName: selectedItem.CtypeName,
        IntervalDays: selectedItem.IntervalDays,
        LastCalibrationDate: selectedItem.LastCalibrationDate,
        NextDueDate: selectedItem.NextDueDate,
        Status: selectedItem.Status,
        Vid: selectedItem.Vid,
        VCode: selectedItem.VCode,
        Vname: selectedItem.Vname,
        AssetCalibration: selectedItem.CtypeName,
        DocNo: selectedItem.DocNo,
      });
      setCalibrationDate(selectedItem.LastCalibrationDate?.split("T")[0] || "");
      setNewNextDueDate(selectedItem.NextDueDate?.split("T")[0] || "");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!asset) {
      toast.warn("Please select an asset.");
      return;
    }

    const payload = {
      Sp_SqlAction: editData ? "update" : "insert",
      LogId: selectedAssetData.LogId || 0,
      AssetId: selectedAssetData.AssetID || asset || 0,
      CtId: selectedAssetData.CtId || selectedAssetData.CTID || 0,
      Vid: selectedAssetData.Vid || 0,
      CalibrationDate: calibrationDate,
      NewNextDueDate: newNextDueDate,
      CertificateNumber: certificateNumber,
      CertificatePath: certificatePath?.name || "",
      Result: result,
      Comments: comments,
      SerCpId: selectedAssetData.SerCpId || 0,
      SerInvNo: serInvNo,
      DocNo: selectedAssetData.DocNo || "",
      AstCalId: selectedAssetData.AstCalId || 0,
      OrderType: selectedAssetData.OrderType || "",
      UserName: userName,
    };

    try {
      const response = await api.SaveCalibrationLogAPI(payload);
      const logId =
        response?.SaveCalibrationLogAPIjs?.Table?.[0]?.LogId ||
        response?.Table?.[0]?.LogId ||
        0;

      if (logId > 0) {
        toast.success(
          editData
            ? `Calibration log updated successfully (Log ID: ${logId})`
            : `Calibration log saved successfully (Log ID: ${logId})`
        );
        navigate("/CalibrationLoglistPage");
      } else {
        toast.error("Failed to save/update calibration log.");
      }
    } catch (error) {
      console.error(error);
      toast.error("Error saving calibration log.");
    }
  };

  const handleClose = () => navigate("/CalibrationLoglistPage");
  const handleBack = () => navigate("/CalibrationLoglistPage");

  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "flex-start",
        padding: "40px",
        minHeight: "100vh",
        fontFamily: "Arial, sans-serif",
        backgroundColor: "#f0f4f8",
      }}
    >
      <form
        onSubmit={handleSubmit}
        style={{
          backgroundColor: "#fff",
          padding: "30px",
          borderRadius: "16px",
          boxShadow: "0 10px 25px rgba(0,0,0,0.1)",
          width: "600px",
        }}
      >
        <button
          type="button"
          onClick={handleBack}
          style={{
            ...styles.button,
            backgroundColor: "#007BFF",
            color: "#fff",
          }}
        >
          Back
        </button>

        <h2 style={{ textAlign: "center", marginBottom: "20px" }}>
          📋 Calibration Log
        </h2>

        {/* Asset */}
        <label>Asset</label>
        <Select
          value={
            assetOptions
              .map((a) => ({
                label: `${a.AssetId} / ${a.AssetNo} / ${a.AssetName} / ${a.SerialNo}`,
                value: a.AssetId,
              }))
              .find((opt) => opt.value === asset) || null
          }
          onChange={handleAssetChange}
          options={assetOptions.map((a) => ({
            label: `${a.AssetId} / ${a.AssetNo} / ${a.AssetName} / ${a.SerialNo}`,
            value: a.AssetId,
          }))}
          styles={selectStyles}
          placeholder="Select Asset..."
        />

        {/* Asset Calibration */}
        <label>Asset Calibration</label>
        <Select
          value={
            assetCalibrationOptions
              .map((a) => ({ label: a.CtypeName, value: a.AstCalId }))
              .find((opt) => opt.value === selectedAssetData.AstCalId) || null
          }
          onChange={handleAssetCalibrationChange}
          options={assetCalibrationOptions.map((a) => ({
            label: a.CtypeName,
            value: a.AstCalId,
          }))}
          styles={selectStyles}
          placeholder="Select Asset Calibration..."
        />

        {/* Party */}
        <label>Party</label>
        <Select
          value={
            partyOptions
              .map((p) => ({
                label: `${p.VCode} / ${p.Vname}`,
                value: p.Vid,
              }))
              .find((opt) => opt.value === selectedAssetData.Vid) || null
          }
          onChange={(selected) => {
            const newVid = selected?.value || 0;
            setSelectedAssetData({
              ...selectedAssetData,
              Vid: newVid,
              Party: selected?.label || "",
            });
            setSerInvNo("");
            setSerInvNoOptions([]);
          }}
          options={partyOptions.map((p) => ({
            label: `${p.VCode} / ${p.Vname}`,
            value: p.Vid,
          }))}
          styles={selectStyles}
          placeholder="Select Party..."
        />

        <label>Calibration Date</label>
        <input
          type="date"
          value={calibrationDate}
          onChange={(e) => setCalibrationDate(e.target.value)}
          style={styles.input}
        />

        <label>Certificate Number</label>
        <input
          type="text"
          value={certificateNumber}
          onChange={(e) => setCertificateNumber(e.target.value)}
          style={styles.input}
        />

        {/* Result */}
        <label>Result</label>
        <Select
          value={
            resultOptions
              .map((r) => ({ label: r.Result, value: r.Result }))
              .find((opt) => opt.value === result) || null
          }
          onChange={(selected) => setResult(selected?.value || "")}
          options={resultOptions.map((r) => ({
            label: r.Result,
            value: r.Result,
          }))}
          styles={selectStyles}
          placeholder="Select Result..."
        />

        <label>Comments</label>
        <input
          type="text"
          value={comments}
          onChange={(e) => setComments(e.target.value)}
          style={styles.input}
        />

        {/* ✅ Attachments now OUTSIDE form submit zone */}
        <div
          onClick={(e) => e.stopPropagation()}
          onFocus={(e) => e.stopPropagation()}
          style={{ margin: "20px 0" }}
        >
    <Attachments
            mdlId="CalibrationLog"
            primeId={selectedAssetData?.LogId || 0}
            primeName={selectedAssetData?.LogId?.toString() || ""}
            uploadedBy={sessionStorage.getItem("userName")}
          />
        
        </div>
        
      

        {/* Service RefNo */}
        <label>Service RefNo</label>
        <Select
          value={
            serInvNoOptions
              .map((s) => ({
                label: `${s.pono} - ${s.Particulars}`,
                value: s.pono,
              }))
              .find((opt) => opt.value === serInvNo) || null
          }
          onChange={(selected) => {
            const selectedRow = serInvNoOptions.find(
              (s) => s.pono === selected?.value
            );
            setSerInvNo(selected?.value || "");
            if (selectedRow) {
              setSelectedAssetData((prev) => ({
                ...prev,
                SerCpId: selectedRow.poid || 0,
                OrderType: selectedRow.ordtype || "",
              }));
            }
          }}
          options={serInvNoOptions.map((s) => ({
            label: `${s.pono} - ${s.Particulars}`,
            value: s.pono,
          }))}
          styles={selectStyles}
          placeholder="Select Service RefNo..."
        />

        <label>New Next Due Date</label>
        <input
          type="date"
          value={newNextDueDate}
          onChange={(e) => setNewNextDueDate(e.target.value)}
          style={styles.input}
        />

        <div style={{ display: "flex", gap: "10px" }}>
          <button type="submit" style={styles.button}>
            {editData ? "Update" : "Submit"}
          </button>

          <button
            type="button"
            onClick={handleClose}
            style={{ ...styles.button, backgroundColor: "#ff6b6b" }}
          >
            Close
          </button>
        </div>
      </form>
    </div>
  );
};

const styles = {
  input: {
    width: "100%",
    padding: "10px",
    marginBottom: "15px",
    borderRadius: "8px",
    border: "1px solid #ccc",
    fontSize: "16px",
  },
  button: {
    flex: 1,
    padding: "12px",
    backgroundColor: "#667eea",
    color: "#fff",
    border: "none",
    borderRadius: "8px",
    fontSize: "16px",
    cursor: "pointer",
  },
};

const selectStyles = {
  control: (provided) => ({
    ...provided,
    marginBottom: "15px",
    borderRadius: "8px",
    borderColor: "#ccc",
    fontSize: "16px",
  }),
};

export default CalibrationLog;


// import React, { useState, useEffect } from "react";
// import { useNavigate, useLocation } from "react-router-dom";
// import Select from "react-select";
// import api from "../../api/api";
// import { toast } from "react-toastify";
// import Attachments  from "../../UserControls/Attachments/Attachments";

// const CalibrationLog = () => {
//   const navigate = useNavigate();
//   const location = useLocation();
//   const editData = location.state?.editData || null;

//   const [asset, setAsset] = useState("");
//   const [certificateNumber, setCertificateNumber] = useState("");
//   const [certificatePath, setCertificatePath] = useState("");
//   const [result, setResult] = useState("");
//   const [comments, setComments] = useState("");
//   const [serInvNo, setSerInvNo] = useState("");
//   const [newNextDueDate, setNewNextDueDate] = useState("");
//   const [calibrationDate, setCalibrationDate] = useState("");

//   const [assetOptions, setAssetOptions] = useState([]);
//   const [assetCalibrationOptions, setAssetCalibrationOptions] = useState([]);
//   const [partyOptions, setPartyOptions] = useState([]);
//   const [resultOptions, setResultOptions] = useState([]);
//   const [serInvNoOptions, setSerInvNoOptions] = useState([]);
//   const [selectedAssetData, setSelectedAssetData] = useState({});
//    const userName = sessionStorage.getItem("userName");

//   // ✅ Pre-fill data on edit
//   useEffect(() => {
//     if (editData) {
//       setAsset(editData.AssetId || "");
//       setCertificateNumber(editData.CertificateNumber || "");
//       setResult(editData.Result || "");
//       setComments(editData.Comments || "");
//       setSerInvNo(editData.SerInvNo || "");
//       setCalibrationDate(
//         editData.CalibrationDate ? editData.CalibrationDate.split("T")[0] : ""
//       );
//       setNewNextDueDate(
//         editData.NewNextDueDate ? editData.NewNextDueDate.split("T")[0] : ""
//       );

//       setSelectedAssetData({
//         ...selectedAssetData,
//         LogId: editData.LogId || 0,
//         AssetID: editData.AssetId || 0,
//         Vid: editData.Vid || 0,
//         AstCalId: editData.AstCalId || 0,
//         SerInvNo: editData.SerInvNo || "",
//         DocNo: editData.DocNo || "",
//         SerCpId: editData.SerCpId || 0,     // ✅ Added
//         OrderType: editData.OrderType || "", // ✅ Added
//       });
//     }
//     // eslint-disable-next-line
//   }, [editData]);

//   // --- Load Assets ---
//   useEffect(() => {
//     const fetchAssets = async () => {
//       try {
//         const response = await api.GetCalibrationlogAPI(userName);
//         const data = response?.Table || [];
//         setAssetOptions(data);
//       } catch (error) {
//         console.error(error);
//         toast.error("Failed to fetch assets.");
//       }
//     };
//     fetchAssets();
//   }, []);

//   // --- Load Asset Calibration, Party, Result ---
//   useEffect(() => {
//     if (!asset) return;

//     const fetchAssetCalibration = async () => {
//       try {
//         const res = await api.GetAssetCaliblogAPI(asset, userName);

//         const table0 = res?.Table || [];
//         const table1 = res?.Table1 || [];
//         const table2 = res?.Table2 || [];

//         setAssetCalibrationOptions(table0);
//         setPartyOptions(table1);
//         setResultOptions(table2);

//         if (table0.length > 0 && !editData) {
//           const first = table0[0];
//           setSelectedAssetData({
//             ...selectedAssetData,
//             AstCalId: first.AstCalId,
//             AssetID: first.AssetID,
//             CTID: first.CTID,
//             CtypeName: first.CtypeName,
//             IntervalDays: first.IntervalDays,
//             LastCalibrationDate: first.LastCalibrationDate,
//             NextDueDate: first.NextDueDate,
//             Status: first.Status,
//             Vid: first.Vid,
//             VCode: first.VCode,
//             Vname: first.Vname,
//             AssetCalibration: first.CtypeName,
//             Party: table1[0]?.Vname || "",
//             Result: table2[0]?.Result || "",
//             DocNo: first.DocNo || "",
//           });
//           setCalibrationDate(first.LastCalibrationDate?.split("T")[0] || "");
//           setNewNextDueDate(first.NextDueDate?.split("T")[0] || "");
//           setResult(table2[0]?.Result || "");
//         }
//       } catch (error) {
//         console.error(error);
//         toast.error("Failed to load calibration details.");
//       }
//     };

//     fetchAssetCalibration();
//     // eslint-disable-next-line
//   }, [asset]);

//   // --- Load Service RefNo when Party (Vid) changes ---
//   useEffect(() => {
//     const fetchServiceRefNos = async () => {
//       if (!selectedAssetData?.Vid) return;
//       try {
//         const res = await api.GetServiceInvoiceAPI(selectedAssetData.Vid);
//         console.log("🔍 Service Invoice API Response:", res);
//         const table =
//           res?.GetServiceInvoiceAPIjs?.Table ||
//           res?.Table ||
//           res?.table ||
//           res?.data ||
//           [];
//         setSerInvNoOptions(table);
//       } catch (error) {
//         console.error(error);
//         toast.error("Failed to load Service RefNo data.");
//       }
//     };
//     fetchServiceRefNos();
//   }, [selectedAssetData?.Vid]);

//   const handleAssetChange = (selected) => {
//     setAsset(selected?.value || "");
//     setSelectedAssetData({});
//     setAssetCalibrationOptions([]);
//   };

//   const handleAssetCalibrationChange = (selected) => {
//     const selectedItem = assetCalibrationOptions.find(
//       (a) => a.AstCalId === selected?.value
//     );
//     if (selectedItem) {
//       setSelectedAssetData({
//         ...selectedAssetData,
//         AstCalId: selectedItem.AstCalId,
//         AssetID: selectedItem.AssetID,
//         CTID: selectedItem.CTID,
//         CtypeName: selectedItem.CtypeName,
//         IntervalDays: selectedItem.IntervalDays,
//         LastCalibrationDate: selectedItem.LastCalibrationDate,
//         NextDueDate: selectedItem.NextDueDate,
//         Status: selectedItem.Status,
//         Vid: selectedItem.Vid,
//         VCode: selectedItem.VCode,
//         Vname: selectedItem.Vname,
//         AssetCalibration: selectedItem.CtypeName,
//         DocNo: selectedItem.DocNo,
//       });
//       setCalibrationDate(selectedItem.LastCalibrationDate?.split("T")[0] || "");
//       setNewNextDueDate(selectedItem.NextDueDate?.split("T")[0] || "");
//     }
//   };

//    const handleSubmit = async (e) => {
//   e.preventDefault();

//   if (!asset) {
//     toast.warn("Please select an asset.");
//     return;
//   }

//   const payload = {
//     Sp_SqlAction: editData ? "update" : "insert",
//     LogId: selectedAssetData.LogId || 0,
//     AssetId: selectedAssetData.AssetID || asset || 0,
//     CtId: selectedAssetData.CtId || selectedAssetData.CTID || 0,
//     Vid: selectedAssetData.Vid || 0,
//     CalibrationDate: calibrationDate,
//     NewNextDueDate: newNextDueDate,
//     CertificateNumber: certificateNumber,
//     CertificatePath: certificatePath?.name || "",
//     Result: result,
//     Comments: comments,
//     SerCpId: selectedAssetData.SerCpId || 0,
//     SerInvNo: serInvNo,
//     DocNo: selectedAssetData.DocNo || "",
//     AstCalId: selectedAssetData.AstCalId || 0,
//     OrderType: selectedAssetData.OrderType || "",
//     UserName:userName,
//   };

//   console.log("📦 Payload Sent:", payload);

//   try {
//     const response = await api.SaveCalibrationLogAPI(payload);
//     const logId =
//       response?.SaveCalibrationLogAPIjs?.Table?.[0]?.LogId ||
//       response?.Table?.[0]?.LogId ||
//       0;

//     if (logId > 0) {
//       toast.success(
//         editData
//           ? `Calibration log updated successfully (Log ID: ${logId})`
//           : `Calibration log saved successfully (Log ID: ${logId})`
//       );
//       navigate("/CalibrationLoglistPage");
//     } else {
//       toast.error("Failed to save/update calibration log.");
//     }
//   } catch (error) {
//     console.error(error);
//     toast.error("Error saving calibration log.");
//   }
// };


//   const handleClose = () => navigate("/CalibrationLoglistPage");
//   const handleBack = () => navigate("/CalibrationLoglistPage");

//   return (
//     <div
//       style={{
//         display: "flex",
//         justifyContent: "center",
//         alignItems: "flex-start",
//         padding: "40px",
//         minHeight: "100vh",
//         fontFamily: "Arial, sans-serif",
//         backgroundColor: "#f0f4f8",
//       }}
//     >
//       <form
//         onSubmit={handleSubmit}
//         style={{
//           backgroundColor: "#fff",
//           padding: "30px",
//           borderRadius: "16px",
//           boxShadow: "0 10px 25px rgba(0,0,0,0.1)",
//           width: "600px",
//         }}
//       >
//         <button
//           type="button"
//           onClick={handleBack}
//           style={{
//             ...styles.button,
//             backgroundColor: "#007BFF",
//             color: "#fff",
//           }}
//         >
//           Back
//         </button>

//         <h2 style={{ textAlign: "center", marginBottom: "20px" }}>
//           📋 Calibration Log
//         </h2>

//         {/* ✅ Asset Dropdown */}
//         <label>Asset</label>
//         <Select
//           value={
//             assetOptions
//               .map((a) => ({
//                 label: `${a.AssetId} / ${a.AssetNo} / ${a.AssetName} / ${a.SerialNo}`,
//                 value: a.AssetId,
//               }))
//               .find((opt) => opt.value === asset) || null
//           }
//           onChange={handleAssetChange}
//           options={assetOptions.map((a) => ({
//             label: `${a.AssetId} / ${a.AssetNo} / ${a.AssetName} / ${a.SerialNo}`,
//             value: a.AssetId,
//           }))}
//           styles={selectStyles}
//           placeholder="Select Asset..."
//         />

//         {/* ✅ Asset Calibration */}
//         <label>Asset Calibration</label>
//         <Select
//           value={
//             assetCalibrationOptions
//               .map((a) => ({ label: a.CtypeName, value: a.AstCalId }))
//               .find((opt) => opt.value === selectedAssetData.AstCalId) || null
//           }
//           onChange={handleAssetCalibrationChange}
//           options={assetCalibrationOptions.map((a) => ({
//             label: a.CtypeName,
//             value: a.AstCalId,
//           }))}
//           styles={selectStyles}
//           placeholder="Select Asset Calibration..."
//         />

//         {/* ✅ Party */}
//         <label>Party</label>
//         <Select
//           value={
//             partyOptions
//               .map((p) => ({
//                 label: `${p.VCode} / ${p.Vname}`,
//                 value: p.Vid,
//               }))
//               .find((opt) => opt.value === selectedAssetData.Vid) || null
//           }
//           onChange={(selected) => {
//             const newVid = selected?.value || 0;
//             setSelectedAssetData({
//               ...selectedAssetData,
//               Vid: newVid,
//               Party: selected?.label || "",
//             });
//             setSerInvNo("");
//             setSerInvNoOptions([]);
//           }}
//           options={partyOptions.map((p) => ({
//             label: `${p.VCode} / ${p.Vname}`,
//             value: p.Vid,
//           }))}
//           styles={selectStyles}
//           placeholder="Select Party..."
//         />

//         <label>Calibration Date</label>
//         <input
//           type="date"
//           value={calibrationDate}
//           onChange={(e) => setCalibrationDate(e.target.value)}
//           style={styles.input}
//         />

//         <label>Certificate Number</label>
//         <input
//           type="text"
//           value={certificateNumber}
//           onChange={(e) => setCertificateNumber(e.target.value)}
//           style={styles.input}
//         />

//         {/* ✅ Result */}
//         <label>Result</label>
//         <Select
//           value={
//             resultOptions
//               .map((r) => ({ label: r.Result, value: r.Result }))
//               .find((opt) => opt.value === result) || null
//           }
//           onChange={(selected) => setResult(selected?.value || "")}
//           options={resultOptions.map((r) => ({
//             label: r.Result,
//             value: r.Result,
//           }))}
//           styles={selectStyles}
//           placeholder="Select Result..."
//         />

//         <label>Comments</label>
//         <input
//           type="text"
//           value={comments}
//           onChange={(e) => setComments(e.target.value)}
//           style={styles.input}
//         />

//                           <Attachments
//                              mdlId="CalibrationLog"
                              
//                              primeId={selectedAssetData.LogId }
//                              primeName={selectedAssetData.AssetID}
//                              uploadedBy={sessionStorage.getItem("userName")}
                             
//                            />
                     

//         {/* <label>Certificate Path</label>
//         <input
//           type="file"
//           onChange={(e) => setCertificatePath(e.target.files[0])}
//           style={styles.input}
//         />
//         {certificatePath && <p>Selected File: {certificatePath.name}</p>} */}

//         {/* ✅ SerInvNo */}
//         <label>Service RefNo</label>
//         <Select
//           value={
//             serInvNoOptions
//               .map((s) => ({
//                 label: `${s.pono} - ${s.Particulars}`,
//                 value: s.pono,
//               }))
//               .find((opt) => opt.value === serInvNo) || null
//           }
//           onChange={(selected) => {
//             const selectedRow = serInvNoOptions.find(
//               (s) => s.pono === selected?.value
//             );
//             setSerInvNo(selected?.value || "");
//             if (selectedRow) {
//               setSelectedAssetData((prev) => ({
//                 ...prev,
//                 SerCpId: selectedRow.poid || 0,
//                 OrderType: selectedRow.ordtype || "",
//               }));
//             }
//           }}
//           options={serInvNoOptions.map((s) => ({
//             label: `${s.pono} - ${s.Particulars}`,
//             value: s.pono,
//           }))}
//           styles={selectStyles}
//           placeholder="Select Service RefNo..."
//         />

//         <label>New Next Due Date</label>
//         <input
//           type="date"
//           value={newNextDueDate}
//           onChange={(e) => setNewNextDueDate(e.target.value)}
//           style={styles.input}
//         />

//         <div style={{ display: "flex", gap: "10px" }}>
//           <button type="submit" style={styles.button}>
//             {editData ? "Update" : "Submit"}
//           </button>

//           <button
//             type="button"
//             onClick={handleClose}
//             style={{ ...styles.button, backgroundColor: "#ff6b6b" }}
//           >
//             Close
//           </button>
//         </div>

//         {Object.entries(selectedAssetData).map(([key, value]) => (
//           <input key={key} type="hidden" name={key} value={value || ""} />
//         ))}
//       </form>
//     </div>
//   );
// };

// const styles = {
//   input: {
//     width: "100%",
//     padding: "10px",
//     marginBottom: "15px",
//     borderRadius: "8px",
//     border: "1px solid #ccc",
//     fontSize: "16px",
//   },
//   button: {
//     flex: 1,
//     padding: "12px",
//     backgroundColor: "#667eea",
//     color: "#fff",
//     border: "none",
//     borderRadius: "8px",
//     fontSize: "16px",
//     cursor: "pointer",
//   },
// };

// const selectStyles = {
//   control: (provided) => ({
//     ...provided,
//     marginBottom: "15px",
//     borderRadius: "8px",
//     borderColor: "#ccc",
//     fontSize: "16px",
//   }),
// };

// export default CalibrationLog;


// import React, { useState, useEffect } from "react";
// import { useNavigate, useLocation } from "react-router-dom";
// import Select from "react-select";
// import api from "../../api/api";
// import { toast } from "react-toastify";

// const CalibrationLog = () => {
//   const navigate = useNavigate();
//   const location = useLocation();
//   const editData = location.state?.editData || null;

//   const [asset, setAsset] = useState("");
//   const [certificateNumber, setCertificateNumber] = useState("");
//   const [certificatePath, setCertificatePath] = useState("");
//   const [result, setResult] = useState("");
//   const [comments, setComments] = useState("");
//   const [serInvNo, setSerInvNo] = useState("");
//   const [newNextDueDate, setNewNextDueDate] = useState("");
//   const [calibrationDate, setCalibrationDate] = useState("");

//   const [assetOptions, setAssetOptions] = useState([]);
//   const [assetCalibrationOptions, setAssetCalibrationOptions] = useState([]);
//   const [partyOptions, setPartyOptions] = useState([]);
//   const [resultOptions, setResultOptions] = useState([]);
//   const [serInvNoOptions, setSerInvNoOptions] = useState([]);
//   const [selectedAssetData, setSelectedAssetData] = useState({});

//   // ✅ Pre-fill data on edit
//   useEffect(() => {
//     if (editData) {
//       setAsset(editData.AssetId || "");
//       setCertificateNumber(editData.CertificateNumber || "");
//       setResult(editData.Result || "");
//       setComments(editData.Comments || "");
//       setSerInvNo(editData.SerInvNo || "");
//       setCalibrationDate(
//         editData.CalibrationDate ? editData.CalibrationDate.split("T")[0] : ""
//       );
//       setNewNextDueDate(
//         editData.NewNextDueDate ? editData.NewNextDueDate.split("T")[0] : ""
//       );

//       setSelectedAssetData({
//         ...selectedAssetData,
//         LogId: editData.LogId || 0,
//         AssetID: editData.AssetId || 0,
//         Vid: editData.Vid || 0,
//         AstCalId: editData.AstCalId || 0,
//         SerInvNo: editData.SerInvNo || "",
//         DocNo: editData.DocNo || "",
//       });
//     }
//     // eslint-disable-next-line
//   }, [editData]);

//   // --- Load Assets ---
//   useEffect(() => {
//     const fetchAssets = async () => {
//       try {
//         const response = await api.GetCalibrationlogAPI("User");
//         const data = response?.Table || [];
//         setAssetOptions(data);
//       } catch (error) {
//         console.error(error);
//         toast.error("Failed to fetch assets.");
//       }
//     };
//     fetchAssets();
//   }, []);

//   // --- Load Asset Calibration, Party, Result, SerInvNo ---
//   useEffect(() => {
//     if (!asset) return;

//    const fetchAssetCalibration = async () => {
//   try {
//     const res = await api.GetAssetCaliblogAPI(asset, "User");

//     const table0 = res?.Table || [];
//     const table1 = res?.Table1 || [];
//     const table22 = res?.Table22 || []; // adjust if backend sends differently
//     const table2 = res?.Table2 || [];   // ✅ FIXED

//     setAssetCalibrationOptions(table0);
//     setPartyOptions(table1);
//     setSerInvNoOptions(table22);
//     setResultOptions(table2);

//     if (table0.length > 0 && !editData) {
//       const first = table0[0];
//       setSelectedAssetData({
//         ...selectedAssetData,
//         AstCalId: first.AstCalId,
//         AssetID: first.AssetID,
//         CTID: first.CTID,
//         CtypeName: first.CtypeName,
//         IntervalDays: first.IntervalDays,
//         LastCalibrationDate: first.LastCalibrationDate,
//         NextDueDate: first.NextDueDate,
//         Status: first.Status,
//         Vid: first.Vid,
//         VCode: first.VCode,
//         Vname: first.Vname,
//         AssetCalibration: first.CtypeName,
//         Party: table1[0]?.Vname || "",
//         SerCpId: table22[0]?.SerCpId || 0,
//         SerInvNo: table22[0]?.SerInvNo || "",
//         Result: table2[0]?.Result || "",
//         DocNo: first.VCode || "",
//       });
//       setCalibrationDate(first.LastCalibrationDate?.split("T")[0] || "");
//       setNewNextDueDate(first.NextDueDate?.split("T")[0] || "");
//       setResult(table2[0]?.Result || "");
//       setSerInvNo(table22[0]?.SerInvNo || "");
//     }
//   } catch (error) {
//     console.error(error);
//     toast.error("Failed to load calibration details.");
//   }
// };


//     fetchAssetCalibration();
//     // eslint-disable-next-line
//   }, [asset]);


//   // --- Load Service RefNo when Party (Vid) changes ---
// useEffect(() => {
//   const fetchServiceRefNos = async () => {
//     if (!selectedAssetData?.Vid) return;
//     try {
//       const res = await api.GetServiceInvoiceAPI(selectedAssetData.Vid);
//       console.log("🔍 Service Invoice API Response:", res); // 👈 ADD THIS LINE
//       const table =
//         res?.GetServiceInvoiceAPIjs?.Table ||
//         res?.Table ||
//         res?.table ||
//         res?.data ||
//         [];
//       setSerInvNoOptions(table);
//     } catch (error) {
//       console.error(error);
//       toast.error("Failed to load Service RefNo data.");
//     }
//   };
//   fetchServiceRefNos();
// }, [selectedAssetData?.Vid]);



//   const handleAssetChange = (selected) => {
//     setAsset(selected?.value || "");
//     setSelectedAssetData({});
//     setAssetCalibrationOptions([]);
//   };

//   const handleAssetCalibrationChange = (selected) => {
//     const selectedItem = assetCalibrationOptions.find(
//       (a) => a.AstCalId === selected?.value
//     );
//     if (selectedItem) {
//       setSelectedAssetData({
//         ...selectedAssetData,
//         AstCalId: selectedItem.AstCalId,
//         AssetID: selectedItem.AssetID,
//         CTID: selectedItem.CTID,
//         CtypeName: selectedItem.CtypeName,
//         IntervalDays: selectedItem.IntervalDays,
//         LastCalibrationDate: selectedItem.LastCalibrationDate,
//         NextDueDate: selectedItem.NextDueDate,
//         Status: selectedItem.Status,
//         Vid: selectedItem.Vid,
//         VCode: selectedItem.VCode,
//         Vname: selectedItem.Vname,
//         AssetCalibration: selectedItem.CtypeName,
//         DocNo: selectedItem.VCode,
//       });
//       setCalibrationDate(selectedItem.LastCalibrationDate?.split("T")[0] || "");
//       setNewNextDueDate(selectedItem.NextDueDate?.split("T")[0] || "");
//     }
//   };

//   const handleSubmit = async (e) => {
//     e.preventDefault();

//     if (!asset) {
//       toast.warn("Please select an asset.");
//       return;
//     }

//     const payload = {
//       Sp_SqlAction: editData ? "update" : "insert",
//       LogId: selectedAssetData.LogId || 0,
//       AssetId: selectedAssetData.AssetID || 0,
//       CtId: selectedAssetData.CTID || 0,
//       Vid: selectedAssetData.Vid || 0,
//       CalibrationDate: calibrationDate,
//       NewNextDueDate: newNextDueDate,
//       CertificateNumber: certificateNumber,
//       CertificatePath: certificatePath?.name || "",
//       Result: result,
//       Comments: comments,
//       SerCpId: selectedAssetData.SerCpId || 0,
//       SerInvNo: serInvNo,
//       DocNo: "",
//       AstCalId: selectedAssetData.AstCalId || 0,
//     };

//     try {
//       const response = await api.SaveCalibrationLogAPI(payload);
//       if (response?.SaveAssetCalibrationAPIjs?.Table?.length > 0) {
//         toast.success(
//           "Calibration log saved! Log ID: " +
//             response.SaveAssetCalibrationAPIjs.Table[0].LogId
//         );
//         navigate("/CalibrationLoglistPage");
//       } else {
//         toast.error("Failed to save calibration log.");
//       }
//     } catch (error) {
//       console.error(error);
//       toast.error("Error saving calibration log.");
//     }
//   };

//   const handleClose = () => navigate("/CalibrationLoglistPage");
//   const handleBack = () => navigate("/CalibrationLoglistPage");

//   return (
//     <div
//       style={{
//         display: "flex",
//         justifyContent: "center",
//         alignItems: "flex-start",
//         padding: "40px",
//         minHeight: "100vh",
//         fontFamily: "Arial, sans-serif",
//         backgroundColor: "#f0f4f8",
//       }}
//     >
//       <form
//         onSubmit={handleSubmit}
//         style={{
//           backgroundColor: "#fff",
//           padding: "30px",
//           borderRadius: "16px",
//           boxShadow: "0 10px 25px rgba(0,0,0,0.1)",
//           width: "600px",
//         }}
//       >
// <button
//   type="button"
//   onClick={handleBack}
//   style={{
//     ...styles.button,
//     backgroundColor: "#007BFF",
//     color: "#fff", // makes text visible on blue
//   }}
// >
//   Back
// </button>


//         <h2 style={{ textAlign: "center", marginBottom: "20px" }}>
//           📋 Calibration Log
//         </h2>

//         {/* ✅ Asset Dropdown */}
//         <label>Asset</label>
//         <Select
//           value={
//             assetOptions
//               .map((a) => ({
//                 label: `${a.AssetId} / ${a.AssetNo} / ${a.AssetName} / ${a.SerialNo}`,
//                 value: a.AssetId,
//               }))
//               .find((opt) => opt.value === asset) || null
//           }
//           onChange={handleAssetChange}
//           options={assetOptions.map((a) => ({
//             label: `${a.AssetId} / ${a.AssetNo} / ${a.AssetName} / ${a.SerialNo}`,
//             value: a.AssetId,
//           }))}
//           styles={selectStyles}
//           placeholder="Select Asset..."
//         />

//         {/* ✅ Asset Calibration */}
//         <label>Asset Calibration</label>
//         <Select
//           value={
//             assetCalibrationOptions
//               .map((a) => ({ label: a.CtypeName, value: a.AstCalId }))
//               .find((opt) => opt.value === selectedAssetData.AstCalId) || null
//           }
//           onChange={handleAssetCalibrationChange}
//           options={assetCalibrationOptions.map((a) => ({
//             label: a.CtypeName,
//             value: a.AstCalId,
//           }))}
//           styles={selectStyles}
//           placeholder="Select Asset Calibration..."
//         />

//         {/* ✅ Party */}
//         <label>Party</label>
//         <Select
//           value={
//             partyOptions
//               .map((p) => ({
//                 label: `${p.VCode} / ${p.Vname}`,
//                 value: p.Vid,
//               }))
//               .find((opt) => opt.value === selectedAssetData.Vid) || null
//           }
//         onChange={(selected) => {
//   const newVid = selected?.value || 0;
//   setSelectedAssetData({
//     ...selectedAssetData,
//     Vid: newVid,
//     Party: selected?.label || "",
//   });
//   setSerInvNo("");          // clear current selection
//   setSerInvNoOptions([]);   // reset dropdown before loading new data
// }}

//           options={partyOptions.map((p) => ({
//             label: `${p.VCode} / ${p.Vname}`,
//             value: p.Vid,
//           }))}
//           styles={selectStyles}
//           placeholder="Select Party..."
//         />

//         <label>Calibration Date</label>
//         <input
//           type="date"
//           value={calibrationDate}
//           onChange={(e) => setCalibrationDate(e.target.value)}
//           style={styles.input}
//         />

//         <label>Certificate Number</label>
//         <input
//           type="text"
//           value={certificateNumber}
//           onChange={(e) => setCertificateNumber(e.target.value)}
//           style={styles.input}
//         />

//         {/* ✅ Result */}
//         <label>Result</label>
//         <Select
//           value={
//             resultOptions
//               .map((r) => ({ label: r.Result, value: r.Result }))
//               .find((opt) => opt.value === result) || null
//           }
//           onChange={(selected) => setResult(selected?.value || "")}
//           options={resultOptions.map((r) => ({
//             label: r.Result,
//             value: r.Result,
//           }))}
//           styles={selectStyles}
//           placeholder="Select Result..."
//         />

//         <label>Comments</label>
//         <input
//           type="text"
//           value={comments}
//           onChange={(e) => setComments(e.target.value)}
//           style={styles.input}
//         />

//         <label>Certificate Path</label>
//         <input
//           type="file"
//           onChange={(e) => setCertificatePath(e.target.files[0])}
//           style={styles.input}
//         />
//         {certificatePath && <p>Selected File: {certificatePath.name}</p>}

//         {/* ✅ SerInvNo */}
//         <label>Service RefNo</label>
//        <Select
//   value={
//     serInvNoOptions
//       .map((s) => ({
//         label: `${s.pono} - ${s.Particulars}`,
//         value: s.pono,
//       }))
//       .find((opt) => opt.value === serInvNo) || null
//   }
//   onChange={(selected) => setSerInvNo(selected?.value || "")}
//   options={serInvNoOptions.map((s) => ({
//     label: `${s.pono} - ${s.Particulars}`,
//     value: s.pono,
//   }))}
//   styles={selectStyles}
//   placeholder="Select Service RefNo..."
// />


//         <label>New Next Due Date</label>
//         <input
//           type="date"
//           value={newNextDueDate}
//           onChange={(e) => setNewNextDueDate(e.target.value)}
//           style={styles.input}
//         />

//         <div style={{ display: "flex", gap: "10px" }}>
//           <button type="submit" style={styles.button}>
//             {editData ? "Update" : "Submit"}
//           </button>

//           <button
//             type="button"
//             onClick={handleClose}
//             style={{ ...styles.button, backgroundColor: "#ff6b6b" }}
//           >
//             Close
//           </button>
//         </div>

//         {Object.entries(selectedAssetData).map(([key, value]) => (
//           <input key={key} type="hidden" name={key} value={value || ""} />
//         ))}
//       </form>
//     </div>
//   );
// };

// const styles = {
//   input: {
//     width: "100%",
//     padding: "10px",
//     marginBottom: "15px",
//     borderRadius: "8px",
//     border: "1px solid #ccc",
//     fontSize: "16px",
//   },
//   button: {
//     flex: 1,
//     padding: "12px",
//     backgroundColor: "#667eea",
//     color: "#fff",
//     border: "none",
//     borderRadius: "8px",
//     fontSize: "16px",
//     cursor: "pointer",
//   },
// };

// const selectStyles = {
//   control: (provided) => ({
//     ...provided,
//     marginBottom: "15px",
//     borderRadius: "8px",
//     borderColor: "#ccc",
//     fontSize: "16px",
//   }),
// };

// export default CalibrationLog;



 